import React, { useState } from 'react';
import { Eye, ShieldCheck, X } from 'lucide-react';
import { api } from '../../lib/api';

/**
 * Inline phone display with privacy gate.
 *
 * - Anonymous visitors see the masked value (e.g. `99XX-XX-XX97`) plus a small
 *   "Show Number" button. Clicking it expands an inline math captcha
 *   ("What is 3 + 4?"). A correct answer swaps in the real number.
 * - Logged-in customers (httpOnly cookie auth) skip the captcha — the backend
 *   already trusts them, so we just call /reveal-mobile and show the number.
 *
 * Props:
 *   supplier.id          – required, used in the reveal endpoint path
 *   supplier.mobile      – the *masked* string returned by GET /api/suppliers/{id}
 *   supplier.mobile_revealed – true when backend already returned the real value
 *                              (i.e. visitor was authenticated)
 */
const RevealMobile = ({ supplier }) => {
  const initial = supplier?.mobile || '';
  const alreadyRevealed = !!supplier?.mobile_revealed;

  const [revealed, setRevealed] = useState(alreadyRevealed ? initial : '');
  const [open, setOpen] = useState(false);          // captcha panel visibility
  const [challenge, setChallenge] = useState(null); // {a, b, token}
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (revealed) {
    // Final state — render the real number as a tappable tel: link.
    const e164 = `+91${revealed}`;
    return (
      <a
        href={`tel:${e164}`}
        className="font-medium text-[#2c54b2] hover:underline"
        data-testid="supplier-mobile-revealed"
      >
        {e164}
      </a>
    );
  }

  const startReveal = async () => {
    setError('');
    setBusy(true);
    try {
      // First try the no-captcha fast path — logged-in customers get a 200 here.
      const r = await api.revealVendorMobile(supplier.id);
      if (r?.mobile) {
        setRevealed(r.mobile);
        return;
      }
    } catch (err) {
      // 400 (captcha needed) is the expected anonymous path — fall through to challenge.
      if (err?.response?.status !== 400) {
        setError(err?.response?.data?.detail || 'Could not load number. Try again.');
        setBusy(false);
        return;
      }
    }
    // Anonymous path — fetch a fresh math challenge.
    try {
      const c = await api.getCaptchaMath();
      setChallenge(c);
      setOpen(true);
    } catch (err) {
      setError('Could not load captcha. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setError('');
    try {
      const r = await api.revealVendorMobile(supplier.id, {
        token: challenge.token,
        answer: Number(answer),
      });
      setRevealed(r.mobile);
      setOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Incorrect answer. Try again.');
      // Issue a fresh challenge so the user can retry without a stale token.
      try {
        const c = await api.getCaptchaMath();
        setChallenge(c);
        setAnswer('');
      } catch (e) {
        // Non-fatal: user can simply re-click "Show Number" to retry.
        console.warn('Failed to refresh captcha challenge', e);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="supplier-mobile-gate">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-900 font-mono tracking-wide" data-testid="supplier-mobile-masked">
          +91 {initial || '99XX-XX-XX99'}
        </span>
        <button
          type="button"
          onClick={startReveal}
          disabled={busy}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#2c54b2] hover:text-[#ee5e23] bg-blue-50 hover:bg-orange-50 border border-blue-200 hover:border-orange-200 px-2 py-1 rounded-full transition-colors disabled:opacity-50"
          data-testid="supplier-mobile-reveal-btn"
        >
          <Eye className="w-3.5 h-3.5" />
          {busy ? 'Loading…' : 'Show Number'}
        </button>
      </div>

      {open && challenge && (
        <div
          className="relative bg-gray-50 border border-gray-200 rounded-md p-3 max-w-xs"
          data-testid="supplier-mobile-captcha"
        >
          <button
            type="button"
            onClick={() => { setOpen(false); setError(''); setAnswer(''); }}
            className="absolute top-1.5 right-1.5 text-gray-400 hover:text-gray-700"
            aria-label="Close"
            data-testid="supplier-mobile-captcha-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="flex items-center gap-1.5 text-[11px] text-gray-600 mb-2">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            Quick check to prevent spam bots
          </div>
          <form onSubmit={submit} className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-800 select-none">
              What is <span className="font-bold">{challenge.a}</span> + <span className="font-bold">{challenge.b}</span>?
            </label>
            <input
              type="number"
              autoFocus
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#ee5e23]"
              data-testid="supplier-mobile-captcha-input"
              required
            />
            <button
              type="submit"
              disabled={busy || !answer}
              className="px-3 py-1 text-xs font-semibold bg-[#ee5e23] hover:bg-[#d24e15] disabled:opacity-50 text-white rounded"
              data-testid="supplier-mobile-captcha-submit"
            >
              {busy ? '…' : 'Verify'}
            </button>
          </form>
          {error && (
            <div className="mt-2 text-[11px] text-red-600" data-testid="supplier-mobile-captcha-error">{error}</div>
          )}
        </div>
      )}
      {error && !open && (
        <div className="text-[11px] text-red-600" data-testid="supplier-mobile-reveal-error">{error}</div>
      )}
    </div>
  );
};

export default RevealMobile;
